// ─── App switcher ─────────────────────────────────────────────────────────────
// Every storefront in the family, for the dropdown in the header. Identical in all
// seven apps (the six CRA shops and the LMU TV storefront inside the hub) — copy it
// over rather than editing one copy, so the menus cannot drift apart again. They had:
// nobody linked ACE DRIVE, ACC ACADEMY was missing two entries, iR DRIVE was listed
// under its old host, and LMU TV was in nobody's menu while its own menu was missing
// AC DRIVE and ACE DRIVE.
//
// The logos are bundled (src/media/app-logos, same files in every app). They used to
// be read from each target site's own /logo192.png, but the servers send
// `Cross-Origin-Resource-Policy: same-origin`, so the browser blocks those images on
// every other domain and the menu showed only alt text. A redesigned logo therefore
// has to be copied into all seven apps' app-logos folders.

import accDriveLogo from "../media/app-logos/acc-drive.png";
import accAcademyLogo from "../media/app-logos/acc-academy.png";
import irDriveLogo from "../media/app-logos/ir-drive.png";
import lmuDriveLogo from "../media/app-logos/lmu-drive.png";
import acDriveLogo from "../media/app-logos/ac-drive.png";
import aceDriveLogo from "../media/app-logos/ace-drive.png";
import accTvLogo from "../media/app-logos/acc-tv.png";
import lmuTvLogo from "../media/app-logos/lmu-tv.png";

export interface AppLink {
  name: string;
  url: string;
  logo: string;
  /** Path prefix, for an app served from a subpath instead of its own host. */
  path?: string;
  /** Other hosts the same app answers on, so its own menu never links to itself. */
  aliases?: string[];
}

export const APPS: AppLink[] = [
  { name: "ACC DRIVE", url: "https://accdrive.com", logo: accDriveLogo },
  { name: "ACC ACADEMY", url: "https://academy.accsolutions.de", logo: accAcademyLogo },
  { name: "iR DRIVE", url: "https://irdrive.de", logo: irDriveLogo },
  { name: "LMU DRIVE", url: "https://lmudrive.com", logo: lmuDriveLogo },
  { name: "AC DRIVE", url: "https://acdrive.simracingsolutions.de", logo: acDriveLogo },
  { name: "ACE DRIVE", url: "https://acedrive.simracingsolutions.de", logo: aceDriveLogo },
  { name: "ACC TV", url: "https://acctv.de", logo: accTvLogo },
  {
    name: "LMU TV",
    url: "https://simracingsolutions.de/lmutv",
    logo: lmuTvLogo,
    path: "/lmutv",
    aliases: ["lmutv.de", "www.lmutv.de", "lmutv.com", "www.lmutv.com"],
  },
];

/**
 * Every app except the one currently being viewed, matched on the host. Doing it by
 * host instead of a per-app constant is what keeps this file byte-identical across the
 * repos. On localhost nothing matches and the full list shows, which is harmless.
 *
 * An app on a subpath (LMU TV on the hub) only counts as "current" when the path
 * matches too — otherwise the hub's own pages would drop it from their menu — and it
 * also hides itself on the alternate hosts it answers on.
 */
export function otherApps(
  host: string = window.location.hostname,
  pathname: string = window.location.pathname
): AppLink[] {
  return APPS.filter((app) => {
    try {
      if (app.aliases?.includes(host)) return false;
      if (new URL(app.url).hostname !== host) return true;
      if (!app.path) return false;
      return !(pathname === app.path || pathname.startsWith(`${app.path}/`));
    } catch {
      return true;
    }
  });
}
